import Phaser from "phaser";

import { MAKERS_CLUB_ART } from "../config/artAssets";

export type MakersClubCircle = "all" | "near" | "far";

export type MakersClubVisualState = {
  selectedCircle: MakersClubCircle;
  totalMembers: number;
  nearMembers: number;
  farMembers: number;
  milestoneTarget: number;
  reducedMotion: boolean;
};

export type MakersClubSceneCallbacks = {
  onReady?(): void;
  onError?(): void;
};

type ClubSlot = {
  circle: Exclude<MakersClubCircle, "all">;
  index: number;
  root: Phaser.GameObjects.Container;
  base: Phaser.GameObjects.Ellipse;
  core: Phaser.GameObjects.Ellipse;
  halo: Phaser.GameObjects.Ellipse;
};

const MAX_INVITATION_SPARKS = 24;

export class MakersClubScene extends Phaser.Scene {
  readonly #callbacks: MakersClubSceneCallbacks;
  readonly #renderScale: number;
  readonly #nearCapacity: number;
  readonly #farCapacity: number;
  #visualState: MakersClubVisualState;
  #layoutWidth = 0;
  #layoutHeight = 0;
  #beaconBaseY = 0;
  #beaconScale = 1;
  #stageScale = 1;

  #background!: Phaser.GameObjects.Image;
  #shade!: Phaser.GameObjects.Rectangle;
  #stageGlow!: Phaser.GameObjects.Arc;
  #stageShadow!: Phaser.GameObjects.Ellipse;
  #beacon!: Phaser.GameObjects.Image;
  #nearSignet!: Phaser.GameObjects.Image;
  #farSignet!: Phaser.GameObjects.Image;
  #invitationSweep!: Phaser.GameObjects.Ellipse;
  #flash!: Phaser.GameObjects.Rectangle;
  #slots: ClubSlot[] = [];
  #sparks: Phaser.GameObjects.Arc[] = [];
  #ambientTweens: Phaser.Tweens.Tween[] = [];

  constructor(
    callbacks: MakersClubSceneCallbacks,
    visualState: MakersClubVisualState,
    renderScale: number,
  ) {
    super({ key: "MakersClub" });
    this.#callbacks = callbacks;
    this.#visualState = visualState;
    const target = Math.max(1, visualState.milestoneTarget);
    this.#nearCapacity = Math.ceil(target / 2);
    this.#farCapacity = Math.floor(target / 2);
    this.#renderScale = renderScale;
  }

  preload(): void {
    this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, () => this.#callbacks.onError?.());
    this.load.image(MAKERS_CLUB_ART.background.key, MAKERS_CLUB_ART.background.url);
    this.load.image(MAKERS_CLUB_ART.beacon.key, MAKERS_CLUB_ART.beacon.url);
    this.load.image(MAKERS_CLUB_ART.nearSignet.key, MAKERS_CLUB_ART.nearSignet.url);
    this.load.image(MAKERS_CLUB_ART.farSignet.key, MAKERS_CLUB_ART.farSignet.url);
  }

  create(): void {
    this.#background = this.add.image(0, 0, MAKERS_CLUB_ART.background.key).setDepth(0);
    this.#shade = this.add.rectangle(0, 0, 10, 10, 0x041f29, 0.12).setOrigin(0).setDepth(1);
    this.#stageGlow = this.add
      .circle(0, 0, 88, 0x62eee0, 0.18)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(2);
    this.#stageShadow = this.add
      .ellipse(0, 0, 230, 42, 0x031820, 0.54)
      .setBlendMode(Phaser.BlendModes.MULTIPLY)
      .setDepth(3);

    this.#slots = [
      ...this.#createSlots("near", this.#nearCapacity),
      ...this.#createSlots("far", this.#farCapacity),
    ];

    this.#beacon = this.add.image(0, 0, MAKERS_CLUB_ART.beacon.key).setDepth(5);
    this.#nearSignet = this.add.image(0, 0, MAKERS_CLUB_ART.nearSignet.key).setDepth(4);
    this.#farSignet = this.add.image(0, 0, MAKERS_CLUB_ART.farSignet.key).setDepth(4);
    this.#invitationSweep = this.add
      .ellipse(0, 0, 210, 68, 0x71f5e7, 0)
      .setStrokeStyle(2, 0xffd082, 0.86)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setVisible(false)
      .setDepth(7);
    this.#flash = this.add.rectangle(0, 0, 10, 10, 0xffe1a2, 0).setOrigin(0).setDepth(9);
    this.#sparks = Array.from({ length: MAX_INVITATION_SPARKS }, (_, index) =>
      this.add
        .circle(0, 0, index % 4 === 0 ? 3 : 2, index % 3 === 0 ? 0xffcf78 : 0x8dfff2, 1)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(8)
        .setVisible(false),
    );

    this.scale.on(Phaser.Scale.Events.RESIZE, this.#layout, this);
    this.#layout({ width: this.scale.width, height: this.scale.height } as Phaser.Structs.Size);
    this.#applyVisualState(false);
    this.#callbacks.onReady?.();
  }

  setVisualState(visualState: MakersClubVisualState): void {
    if (
      this.#visualState.selectedCircle === visualState.selectedCircle &&
      this.#visualState.totalMembers === visualState.totalMembers &&
      this.#visualState.nearMembers === visualState.nearMembers &&
      this.#visualState.farMembers === visualState.farMembers &&
      this.#visualState.milestoneTarget === visualState.milestoneTarget &&
      this.#visualState.reducedMotion === visualState.reducedMotion
    ) return;
    this.#visualState = visualState;
    if (this.sys.isActive()) this.#applyVisualState(true);
  }

  debugSnapshot(): Record<string, unknown> {
    return {
      selectedCircle: this.#visualState.selectedCircle,
      beaconY: Number(this.#beacon.y.toFixed(3)),
      beaconBaseY: this.#beaconBaseY,
      nearSignetY: Number(this.#nearSignet.y.toFixed(3)),
      farSignetY: Number(this.#farSignet.y.toFixed(3)),
      reducedMotion: this.#visualState.reducedMotion,
    };
  }

  playInvitation(): void {
    if (!this.sys.isActive() || this.#visualState.reducedMotion) return;
    this.#ambientTweens.forEach((tween) => tween.stop());
    this.#ambientTweens = [];
    this.tweens.killTweensOf(this.#beacon);
    this.tweens.killTweensOf(this.#stageGlow);
    this.tweens.killTweensOf(this.#invitationSweep);
    this.tweens.killTweensOf(this.#flash);

    this.#invitationSweep
      .setPosition(this.#layoutWidth / 2, this.#beaconBaseY + 64)
      .setVisible(true)
      .setAlpha(0.9)
      .setScale(0.36);
    this.#flash.setAlpha(0.16);
    this.tweens.add({ targets: this.#flash, alpha: 0, duration: 520, ease: "Quad.easeOut" });
    this.tweens.add({
      targets: this.#invitationSweep,
      alpha: 0,
      scaleX: 1.72,
      scaleY: 1.35,
      duration: 760,
      ease: "Cubic.easeOut",
      onComplete: () => this.#invitationSweep.setVisible(false),
    });
    this.tweens.add({
      targets: this.#beacon,
      y: this.#beaconBaseY - 8,
      scaleX: this.#beaconScale * 1.055,
      scaleY: this.#beaconScale * 1.055,
      duration: 300,
      yoyo: true,
      ease: "Sine.easeOut",
      onComplete: () => this.#restartAmbientMotion(),
    });
    this.tweens.add({
      targets: this.#stageGlow,
      alpha: 0.62,
      scale: this.#stageScale * 1.2,
      duration: 360,
      yoyo: true,
      ease: "Sine.easeOut",
    });
    this.#emitInvitationBurst();
  }

  #createSlots(circle: ClubSlot["circle"], count: number): ClubSlot[] {
    return Array.from({ length: count }, (_, index) => {
      const halo = this.add
        .ellipse(0, 0, 24, 11, 0x62f2e5, 0)
        .setBlendMode(Phaser.BlendModes.ADD);
      const base = this.add.ellipse(0, 0, 14, 7, 0x5c3428, 0.82).setStrokeStyle(1, 0xd49a58, 0.8);
      const core = this.add.ellipse(0, -1, 7, 4, 0x72f5e9, 0.92);
      const root = this.add.container(0, 0, [halo, base, core]).setDepth(3);
      return { circle, index, root, base, core, halo };
    });
  }

  #layout(size: Phaser.Structs.Size): void {
    const physicalWidth = Math.round(size.width);
    const physicalHeight = Math.round(size.height);
    const width = Math.round(physicalWidth / this.#renderScale);
    const height = Math.round(physicalHeight / this.#renderScale);
    if (width <= 0 || height <= 0 || (width === this.#layoutWidth && height === this.#layoutHeight)) return;
    this.#layoutWidth = width;
    this.#layoutHeight = height;
    this.cameras.main
      .setViewport(0, 0, physicalWidth, physicalHeight)
      .setZoom(this.#renderScale)
      .centerOn(width / 2, height / 2);

    const backgroundScale = Math.max(
      width / MAKERS_CLUB_ART.background.width,
      height / MAKERS_CLUB_ART.background.height,
    );
    this.#background
      .setPosition(width / 2, height / 2 - Math.min(42, height * 0.075))
      .setScale(backgroundScale);
    this.#shade.setSize(width, height);
    this.#flash.setSize(width, height);

    const compactScale = Phaser.Math.Clamp(width / 360, 0.86, 1.14);
    this.#stageScale = compactScale;
    const stageY = Math.round(height * (height <= 450 ? 0.73 : 0.72));
    const beaconWidth = Phaser.Math.Clamp(width * 0.61, 182, 246);
    this.#beaconScale = beaconWidth / MAKERS_CLUB_ART.beacon.width;
    this.#beaconBaseY = stageY - 58 * compactScale;
    this.#beacon.setPosition(width / 2, this.#beaconBaseY).setScale(this.#beaconScale);
    this.#stageGlow.setPosition(width / 2, stageY - 44 * compactScale).setScale(compactScale);
    this.#stageShadow
      .setPosition(width / 2, stageY + 25 * compactScale)
      .setScale(compactScale);
    this.#invitationSweep.setPosition(width / 2, stageY + 17 * compactScale);

    const nearHeight = 75 * compactScale;
    this.#nearSignet
      .setPosition(width * 0.15, stageY - 31 * compactScale)
      .setScale(nearHeight / MAKERS_CLUB_ART.nearSignet.height);
    const farHeight = 60 * compactScale;
    this.#farSignet
      .setPosition(width * 0.85, stageY - 27 * compactScale)
      .setScale(farHeight / MAKERS_CLUB_ART.farSignet.height);

    this.#positionSlots("near", width / 2, stageY + 9 * compactScale, width * 0.29, 25 * compactScale);
    this.#positionSlots("far", width / 2, stageY + 12 * compactScale, width * 0.405, 45 * compactScale);
    this.#applyVisualState(false);
  }

  #positionSlots(
    circle: ClubSlot["circle"],
    centerX: number,
    centerY: number,
    radiusX: number,
    radiusY: number,
  ): void {
    const slots = this.#slots.filter((slot) => slot.circle === circle);
    slots.forEach((slot, index) => {
      const angle = -Math.PI / 2 + (Math.PI * 2 * index) / Math.max(1, slots.length);
      const yOffset = Math.sin(angle) * radiusY;
      slot.root
        .setPosition(centerX + Math.cos(angle) * radiusX, centerY + yOffset)
        .setDepth(yOffset > 2 ? 6 : 3)
        .setScale(circle === "near" ? 1 : 0.92);
    });
  }

  #applyVisualState(animateTransition: boolean): void {
    this.#ambientTweens.forEach((tween) => tween.stop());
    this.#ambientTweens = [];
    this.tweens.killTweensOf(this.#beacon);
    this.tweens.killTweensOf(this.#nearSignet);
    this.tweens.killTweensOf(this.#farSignet);
    this.tweens.killTweensOf(this.#stageGlow);

    const empty = this.#visualState.totalMembers === 0;
    this.#beacon
      .setPosition(this.#layoutWidth / 2, this.#beaconBaseY)
      .setScale(this.#beaconScale)
      .setAlpha(empty ? 0.76 : 1)
      .setTint(empty ? 0x9ab2af : 0xffffff);
    this.#stageGlow
      .setAlpha(empty ? 0.08 : 0.24)
      .setScale(this.#stageScale)
      .setFillStyle(empty ? 0x799b9a : 0x62eee0, empty ? 0.08 : 0.2);

    this.#slots.forEach((slot) => {
      const members = slot.circle === "near" ? this.#visualState.nearMembers : this.#visualState.farMembers;
      const filled = slot.index < members;
      const selected = this.#visualState.selectedCircle === "all" || this.#visualState.selectedCircle === slot.circle;
      const lightColor = slot.circle === "near" ? 0x8ffff1 : 0xffce7a;
      slot.root.setAlpha(selected ? 1 : 0.35);
      slot.base.setFillStyle(filled ? 0x765038 : 0x263f43, filled ? 0.9 : 0.72);
      slot.core.setFillStyle(filled ? lightColor : 0x607779, filled ? 1 : 0.32).setAlpha(filled ? 1 : 0.38);
      slot.halo
        .setFillStyle(lightColor, filled && selected ? 0.3 : 0)
        .setAlpha(filled && selected ? 0.38 : 0)
        .setVisible(filled);
    });

    const nearSelected = this.#visualState.selectedCircle === "near";
    const farSelected = this.#visualState.selectedCircle === "far";
    this.#nearSignet.setAlpha(farSelected ? 0.32 : nearSelected ? 1 : 0.7).clearTint();
    this.#farSignet.setAlpha(nearSelected ? 0.32 : farSelected ? 1 : 0.7).clearTint();
    if (empty) {
      this.#nearSignet.setTint(0x748b8b).setAlpha(0.36);
      this.#farSignet.setTint(0x748b8b).setAlpha(0.36);
    }

    if (animateTransition && !this.#visualState.reducedMotion) {
      const selected = nearSelected ? this.#nearSignet : farSelected ? this.#farSignet : null;
      if (selected !== null) {
        const startScaleX = selected.scaleX;
        const startScaleY = selected.scaleY;
        selected.setScale(startScaleX * 0.9, startScaleY * 0.9);
        this.tweens.add({
          targets: selected,
          scaleX: startScaleX,
          scaleY: startScaleY,
          duration: 320,
          ease: "Back.easeOut",
        });
      }
    }
    this.#restartAmbientMotion();
  }

  #restartAmbientMotion(): void {
    this.#ambientTweens.forEach((tween) => tween.stop());
    this.#ambientTweens = [];
    this.tweens.killTweensOf(this.#beacon);
    this.tweens.killTweensOf(this.#stageGlow);
    this.#beacon.setY(this.#beaconBaseY);
    if (this.#visualState.reducedMotion) return;

    this.#ambientTweens.push(
      this.tweens.add({
        targets: this.#beacon,
        y: this.#beaconBaseY - 4,
        duration: 2650,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      }),
      this.tweens.add({
        targets: this.#stageGlow,
        alpha: this.#visualState.totalMembers === 0 ? 0.13 : 0.38,
        scale: this.#stageScale * 1.08,
        duration: 1900,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      }),
    );

    this.#slots.forEach((slot, index) => {
      if (!slot.halo.visible || slot.root.alpha < 0.5) return;
      this.#ambientTweens.push(
        this.tweens.add({
          targets: slot.halo,
          alpha: 0.7,
          scaleX: 1.22,
          scaleY: 1.14,
          duration: 1050 + (index % 4) * 140,
          delay: index * 80,
          yoyo: true,
          repeat: -1,
          ease: "Sine.easeInOut",
        }),
      );
    });
  }

  #emitInvitationBurst(): void {
    const originX = this.#layoutWidth / 2;
    const originY = this.#beaconBaseY - 18;
    this.#sparks.forEach((spark, index) => {
      const angle = -Math.PI * 0.92 + (Math.PI * 1.84 * index) / Math.max(1, this.#sparks.length - 1);
      const distance = 54 + (index % 6) * 10;
      this.tweens.killTweensOf(spark);
      spark
        .setPosition(originX, originY)
        .setVisible(true)
        .setAlpha(1)
        .setScale(index % 5 === 0 ? 1.3 : 0.85);
      this.tweens.add({
        targets: spark,
        x: originX + Math.cos(angle) * distance,
        y: originY + Math.sin(angle) * distance * 0.72 - 12,
        alpha: 0,
        scale: 0.28,
        duration: 560 + (index % 5) * 55,
        ease: "Cubic.easeOut",
        onComplete: () => spark.setVisible(false),
      });
    });
  }
}
